import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Popup, Button } from "devextreme-react";
import { confirm, alert } from "devextreme/ui/dialog";
import {
    Column,
    DataGrid,
    Sorting,
    LoadPanel as GridLoadPanel,
    Scrolling,
    Editing,
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { isValidGuid } from "../../../../../common/Utils";
import "../../../../css/omstyle.css";

const PM_REQUIREMENTS_LAYOUT_ID = "A1B2C3D4-E5F6-4A7B-8C9D-0E1F2A3B4C5D";
const POPUP_TITLE = "PM Production Input/Output Requirements";
const ACCEPT_PROMPT = "Accept Production Input/Output Requirements?";
const LAYOUT_FLEX_COLUMN = { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 };
const LAYOUT_SCROLL_AREA = { flex: "1 1 0", minHeight: 0, overflow: "auto", padding: "12px" };
const LAYOUT_FOOTER = { flexShrink: 0, textAlign: "center", padding: "12px 16px", borderTop: "1px solid #eee", backgroundColor: "#fff" };



/** Maps a kit grid row to ImItemKittDetailDto-shaped payload for create-PM (RItem, RLot, Quantity). */
function mapGridRowToKitDetail(row) {
    const total = row.RequiredTotalQuantity ?? row.requiredTotalQuantity;
    return {
        RItem: row.RItem ?? row.rItem,
        RLot: row.RLot ?? row.rLot ?? null,
        Quantity: total,
        ItemCode: row.ItemCode ?? row.itemCode,
        LotCode: row.LotCode ?? row.lotCode,
        NetAvailable: row.NetAvailable ?? row.netAvailable,
    };
}

/** Delphi IsTryingToUseMoreThanNetAvailable: true if any kit row has TotalRequiredQuantity > NetAvailable. */
function isTryingToUseMoreThanNetAvailable(kitDetails) {
    if (!kitDetails?.length) return false;
    return kitDetails.some((k) => {
        const net = k.NetAvailable ?? k.netAvailable;
        const qty = k.Quantity ?? k.quantity;
        return net != null && Number(qty) > Number(net);
    });
}

/** Builds kitDetails array from the grid's current items. */
function buildKitDetailsFromGrid(gridInstance) {
    const store = gridInstance?.getDataSource();
    const items = store?.items() || [];
    return items.map((row) => mapGridRowToKitDetail(row)).filter((k) => isValidGuid(k.RItem));
}

/**
 * Delphi uIMD0085 cdsHeaderTargetOutputChange: derive start quantity from target output.
 * Legacy UI uses whole-number start (e.g. target 5 @ yield 68.85% → start 8, not 7.3).
 * ab_Round(..., 1, rdUp) in Pascal rounds to one decimal; production quantity aligns with integer ceil here.
 */
function startQuantityFromTargetOutput(target, yieldPct) {
    const y = Number(yieldPct);
    const t = Number(target);
    if (!Number.isFinite(y) || y <= 0 || !Number.isFinite(t)) return 0;
    const raw = t / (y / 100);
    return Math.ceil(raw - 1e-12);
}

/** Delphi LoadKitDetails / BuildProductionManagementFormDataAsync: TargetOutput = StartQuantity × Yield%. */
function targetOutputFromStartQuantity(start, yieldPct) {
    const s = Number(start);
    const y = Number(yieldPct);
    if (!Number.isFinite(s) || !Number.isFinite(y) || y <= 0) return 0;
    return Math.round(s * (y / 100) * 100) / 100;
}

/**
 * Delphi cdsHeaderTargetOutputChange / LoadKitDetails:
 * RequiredTotalQuantity = RequiredUnitQuantity * StartQuantity (per kit line).
 */
function deriveKitDetailRows(kitDetails, startQuantity) {
    if (!Array.isArray(kitDetails) || kitDetails.length === 0) return [];
    const start = Number(startQuantity);
    const s = Number.isFinite(start) ? start : 0;
    return kitDetails.map((row, idx) => {
        const unit =
            Number(row.RequiredUnitQuantity ?? row.requiredUnitQuantity ?? row.Quantity ?? row.quantity ?? 0) || 0;
        const totalRaw = unit * s;
        const total = Math.round(totalRaw * 100) / 100;
        const key =
            row.key ??
            `${row.RItem ?? row.rItem ?? "row"}-${row.RLot ?? row.rLot ?? ""}-${idx}`;
        return {
            ...row,
            key,
            RequiredUnitQuantity: unit,
            RequiredTotalQuantity: total,
        };
    });
}

const PMRequirementsLayout = [
    {
        groupId: 1,
        label: "Item Configuration",
        fields: [
            { name: "soldItemCode", tableName: "PMRequirements", label: "Sales Item", type: "text", required: false, visible: true, readOnly: true },
            { name: "prodItemCode", tableName: "PMRequirements", label: "Prod. Item", type: "text", required: false, visible: true, readOnly: true },
            { name: "kitCode", tableName: "PMRequirements", label: "Kit Code", type: "text", required: false, visible: true, readOnly: true },
        ],
    },
    {
        groupId: 2,
        label: "Sales Item Status",
        fields: [
            { name: "qtyOrdered", tableName: "PMRequirements", label: "Qty Ordered", type: "number", required: false, visible: true, readOnly: true, format: { type: "fixedPoint", precision: 2 } },
            { name: "netAvailable", tableName: "PMRequirements", label: "Net Available", type: "number", required: false, visible: true, readOnly: true, format: { type: "fixedPoint", precision: 2, useGrouping: true } },
        ],
    },
    {
        groupId: 3,
        label: "Product Group Configuration",
        fields: [
            { name: "productGroup", tableName: "PMRequirements", label: "Product Group", type: "text", required: false, visible: true, readOnly: true },
            { name: "lossPct", tableName: "PMRequirements", label: "Loss %", type: "number", required: false, visible: true, readOnly: true, format: { type: "fixedPoint", precision: 2 } },
            { name: "yieldPct", tableName: "PMRequirements", label: "Yield %", type: "number", required: false, visible: true, readOnly: true, format: { type: "fixedPoint", precision: 2 } },
        ],
    },
    {
        groupId: 4,
        label: "Production Quantity",
        fields: [
            { name: "startQuantity", tableName: "PMRequirements", label: "Start Quantity", type: "number", required: false, visible: true, readOnly: true, format: { type: "fixedPoint", precision: 2 } },
            { name: "maxProdQty", tableName: "PMRequirements", label: "Max Prod.", type: "number", required: false, visible: true, readOnly: true, format: { type: "fixedPoint", precision: 2 } },
            { name: "targetOutput", tableName: "PMRequirements", label: "Target Output", type: "number", required: false, visible: true, readOnly: false, format: { type: "fixedPoint", precision: 2 } },
        ],
    },
    {
        groupId: 5,
        label: "Dates from Transaction Date",
        fields: [
            { name: "transactionStartDate", tableName: "PMRequirements", label: "Start Date", type: "datetime", required: false, visible: true, readOnly: true },
            { name: "transactionFinishDate", tableName: "PMRequirements", label: "Finish Date", type: "datetime", required: false, visible: true, readOnly: true },
        ],
    },
    {
        groupId: 6,
        label: "Dates from Shipping Date",
        fields: [
            { name: "shippingStartDate", tableName: "PMRequirements", label: "Start Date", type: "datetime", required: false, visible: true, readOnly: true },
            { name: "shippingFinishDate", tableName: "PMRequirements", label: "Finish Date", type: "datetime", required: false, visible: true, readOnly: true },
        ],
    },
];

const PMProductionRequirementsModal = ({
    visible,
    onHiding,
    formData,
    onYes,
    accessToken,
    currentUser,
    DYNAMICFORMSETTING,
}) => {
    const [formFields, setformFields] = useState({});
    const formRefs = useRef({});
    const kitGridRef = useRef(null);
    useEffect(() => {
        if (!formData) return;
        const yld = formData.YieldPct != null ? Number(formData.YieldPct) : 0;

        const rawStart = formData.StartQuantity ?? formData.startQuantity;
        const rawTarget = formData.TargetOutput ?? formData.targetOutput;

        const hasStart = rawStart != null && rawStart !== "";
        const hasTarget = rawTarget != null && rawTarget !== "";

        let start = hasStart ? Number(rawStart) : NaN;
        let target = hasTarget ? Number(rawTarget) : NaN;

        /* No start from API: derive from target (same event as changing target in the form). */
        if (!Number.isFinite(start) && Number.isFinite(target) && yld > 0) {
            start = startQuantityFromTargetOutput(target, yld);
        }
        if (!Number.isFinite(start)) start = 0;

        /*
         * Delphi LoadKitDetails + C# usage branch: TargetOutput always tracks StartQuantity × Yield%.
         * Keeps API production qty as the driver so we do not show inverse-rounded start (e.g. 7.3 vs 8).
         */
        if (Number.isFinite(start) && yld > 0) {
            target = targetOutputFromStartQuantity(start, yld);
        } else if (!Number.isFinite(target)) {
            target = 0;
        }

        setformFields({
            soldItemCode: formData.SoldItemCode,
            prodItemCode: formData.ProdItemCode,
            kitCode: formData.KitCode,
            qtyOrdered: formData.QtyOrdered,
            netAvailable: formData.ItemNetAvailable ?? formData.NetAvailable,
            productGroup: formData.PGCode,
            lossPct: formData.LossPct,
            yieldPct: formData.YieldPct,
            startQuantity: start,
            maxProdQty: formData.MaxProdQty,
            targetOutput: target,
            transactionStartDate: formData.TransactionStartDate,
            transactionFinishDate: formData.TransactionFinishDate,
            shippingStartDate: formData.ShippingStartDate,
            shippingFinishDate: formData.ShippingFinishDate,
        });
    }, [formData]);
    const handleFieldChange = useCallback((name, value) => {
        setformFields((prev) => {
            const yieldPct = Number(prev.yieldPct) || 0;
            if (name === "targetOutput") {
                const target = Number(value) || 0;
                const start =
                    yieldPct > 0 ? startQuantityFromTargetOutput(target, yieldPct) : Number(prev.startQuantity) || 0;
                return { ...prev, targetOutput: target, startQuantity: start };
            }
            return { ...prev, [name]: value };
        });
    }, []);

    /** Kit grid rows stay in sync with startQuantity (Delphi cdsHeaderTargetOutputChange). */
    const computedKitRows = useMemo(() => {
        const rawApiStart = formData?.StartQuantity ?? formData?.startQuantity;
        const startRaw =
            formFields?.startQuantity != null && formFields?.startQuantity !== ""
                ? Number(formFields.startQuantity)
                : rawApiStart != null && rawApiStart !== ""
                  ? Number(rawApiStart)
                  : 0;
        return deriveKitDetailRows(formData?.KitDetails, startRaw);
    }, [formData?.KitDetails, formData?.StartQuantity, formFields?.startQuantity]);

    const kitDataSource = useMemo(() => {
        if (!computedKitRows?.length) return null;
        return new DataSource({ store: computedKitRows, key: "key" });
    }, [computedKitRows]);

    const handleYes = useCallback(async () => {
        if (!formData) return;
        const gridInstance = kitGridRef.current?.instance();
        const requestProductionQty = Number(formFields.startQuantity) || 0;
        const kitDetails = gridInstance ? buildKitDetailsFromGrid(gridInstance) : [];
        const overNetAvailable = isTryingToUseMoreThanNetAvailable(kitDetails);
        const checkKit = formData.CheckKitAvailability ?? "";

        if (overNetAvailable && checkKit === "C") {
            alert(
                "You are trying to use more inputs than what you currently have on Net Available.",
                "Production Input/Output Requirements"
            );
            return;
        }
        if (overNetAvailable && checkKit === "B") {
            const userConfirmed = await confirm(
                "You are trying to use more inputs than what you currently have on Net Available. Do you want to continue?",
                "Production Input/Output Requirements"
            );
            if (!userConfirmed) return;
        }

        const productionItemRowId = formData.ProdItemRowId || formData.SoldItemRowId || null;
        onYes({
            headerRowId: formData.HeaderRowId,
            detailRowId: formData.DetailRowId,
            isFromOption: true,
            requestProductionQty: requestProductionQty,
            productionItemRowId,
            kitDetails,
        });
        onHiding();
    }, [formData, formFields.startQuantity, onYes, onHiding]);

    if (!formData) return null;

    return (
        <Popup
            visible={visible}
            onHiding={onHiding}
            title={POPUP_TITLE}
            width="90vw"
            height="85vh"
            showCloseButton
            dragEnabled
        >
            <div style={LAYOUT_FLEX_COLUMN}>
                <div style={LAYOUT_SCROLL_AREA}>
                    <div className="row" style={{ marginBottom: "12px" }}>
                        <FormField
                            currrentUser={currentUser || { access_token: accessToken, profile: {} }}
                            layoutId={PM_REQUIREMENTS_LAYOUT_ID}
                            layoutName="PMProductionRequirementsForm"
                            defaultFormSchema={PMRequirementsLayout}
                            formFields={formFields}
                            setformFields={setformFields}
                            handleFieldValueChange={handleFieldChange}
                            DYNAMICFORMSETTING={DYNAMICFORMSETTING || "No"}
                            formRefs={formRefs}
                            onFormLoad={() => {}}
                            noSpacingBeforeFirstRow={true}
                        />
                    </div>
                    <div style={{ paddingTop: "8px" }}>
                        <DataGrid
                            ref={kitGridRef}
                            dataSource={kitDataSource}
                            showBorders={true}
                            columnAutoWidth={true}
                            height={320}
                            showColumnHeaders={true}
                            paging={false}
                        >
                            <Editing mode="cell" allowUpdating={true} />
                            <Scrolling mode="standard" />
                            <Sorting mode="single" />
                            <GridLoadPanel enabled={true} />
                            <Column dataField="ItemCode" caption="Item Code" dataType="string" allowEditing={false} />
                            <Column dataField="LotCode" caption="Lot Code" dataType="string" allowEditing={false} />
                            <Column dataField="NetAvailable" caption="Net Available" dataType="number" format={{ type: "fixedPoint", precision: 2 }} allowEditing={false} />
                            <Column dataField="RequiredUnitQuantity" caption="Unit Quantity" dataType="number" format={{ type: "fixedPoint", precision: 2 }} allowEditing={false} />
                            <Column dataField="RequiredTotalQuantity" caption="Total Required Quantity" dataType="number" format={{ type: "fixedPoint", precision: 2 }} allowEditing={true} />
                        </DataGrid>
                    </div>
                </div>
                <div style={LAYOUT_FOOTER}>
                    <div style={{ marginBottom: "8px" }}>{ACCEPT_PROMPT}</div>
                    <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                        <Button text="Yes" type="default" icon="check" onClick={handleYes} />
                        <Button text="No"  icon="close" onClick={onHiding} />
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default PMProductionRequirementsModal;
